import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Table from '@mui/material/Table';

import type { ActionItemSelection } from 'src/types/myFollowUp';
import ErrorRow from 'src/components/followUp/TicketOverviewPage/TicketTable/ErrorRow';
import HeaderTableCell from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/components/HeaderCell';
import TablePagination from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/components/TablePagination';

import useFollowUpActionTable from './FollowUpActionTableContext';
import FollowUpActionTableRow from './TableRow';

import { styles } from './FollowUpActionTable.styles';

const TableHeaderMap: Record<string, { columnKey: string; minWidth: string }> =
  {
    id: { columnKey: 'id', minWidth: '7ch' },
    orgUnits: { columnKey: 'org_units', minWidth: '12ch' },
    kpi: { columnKey: 'kpi__shortcut', minWidth: '9ch' },
    actionPlanTitle: { columnKey: 'action_plan_title', minWidth: '12ch' },
    followUpOwner: {
      columnKey: 'follow_up_owner',
      minWidth: '18ch',
    },
    created: { columnKey: 'created_at', minWidth: '8ch' },
    lastUpdated: { columnKey: 'last_modified_at', minWidth: '8ch' },
    dueAt: { columnKey: 'due_at', minWidth: '8ch' },
    status: { columnKey: 'status', minWidth: '9ch' },
  };

interface FollowUpActionTableProps {
  onRowSelect: (selectedRow: ActionItemSelection) => void;
}

const FollowUpActionTable: FC<FollowUpActionTableProps> = ({ onRowSelect }) => {
  const { t } = useTranslation('followUpActions');
  const { followUpActions, error, loading, pageSize, offset, setOffset } =
    useFollowUpActionTable();

  return (
    <Box sx={(theme) => ({ marginX: theme.spacing(4) })}>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Box sx={{ overflow: 'scroll', maxWidth: '100%' }}>
          <Table>
            <TableHead
              sx={(theme) => ({ backgroundColor: theme.colors.neutral[50] })}
            >
              <TableRow>
                {Object.entries(TableHeaderMap).map(
                  ([translationKey, { columnKey, minWidth }]) => (
                    <HeaderTableCell
                      key={columnKey}
                      columnKey={columnKey}
                      minWidth={minWidth}
                      isFollowUpActionTable
                    >
                      {t(`tableHeader.${translationKey}`)}
                    </HeaderTableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {error ? (
                <ErrorRow text={error} />
              ) : loading ? (
                <TableRow>
                  <TableCell colSpan={9} sx={styles.loadingCell}>
                    <CircularProgress aria-label="spinner" />
                  </TableCell>
                </TableRow>
              ) : followUpActions.results.length ? (
                followUpActions.results.map((followUpAction) => (
                  <FollowUpActionTableRow
                    key={followUpAction.id}
                    {...followUpAction}
                    onClick={() =>
                      onRowSelect({
                        surveyId: 0,
                        actionId: followUpAction.id,
                        type: 'follow-up',
                      })
                    }
                  />
                ))
              ) : (
                <ErrorRow text={t('noTicketsFound')} />
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          count={followUpActions.count}
          previous={followUpActions.previous}
          next={followUpActions.next}
          offset={offset}
          setOffset={setOffset}
          pageSize={pageSize}
        />
      </TableContainer>
    </Box>
  );
};
export default FollowUpActionTable;
